/* El MIC/DTA · Manifiesto Internacional de Carga y Declaración de Tránsito.
 *
 * ── DE DÓNDE SALEN LOS CAMPOS ────────────────────────────────────────────────
 *
 * Del MIC real AR.142.067170 (25/08/2026). 41 casillas numeradas y bilingües.
 * Ninguna etiqueta inventada, igual que con el CRT.
 *
 * ── LA CUENTA ES TODO EL ASUNTO ──────────────────────────────────────────────
 *
 * Nico: «si el tránsito tiene 10 bultos, la sumatoria de los tres MICs tiene
 * que tener 10 bultos». Hoy esa cuenta la lleva una persona y es el error que
 * más caro sale: no se nota al escribirlo, se nota en la frontera.
 *
 * Acá se calcula, y en la base hay un trigger que impide pasarse. Lo de este
 * archivo es para DECIRLO ANTES —cuánto falta, cuánto sobra, con qué hoja va—
 * porque un error de Postgres no le explica nada a nadie.
 *
 * ── PASARSE SE IMPIDE; FALTAR SE AVISA ───────────────────────────────────────
 *
 * No son simétricos y no hay que tratarlos igual. Los MIC se emiten de a uno,
 * así que estar corto es el estado NORMAL mientras se reparte: bloquearlo haría
 * imposible emitir el primero. Pasarse, en cambio, no tiene ningún momento en
 * que sea correcto.
 */

import { aNumero, comoEntero, comoMiles, comoPeso, proximoNumero } from "./crt";
import { contenedorUnico, sinContenedores, vaEnContenedores } from "./micContenedores";

/* ── La capacidad de arrastre, campo 13 ───────────────────────────────────
 *
 * Fabián, 21/09/2026, contestando qué poner: *«campo 13 capacidad arrastre es
 * de 25 TON para todos»*.
 *
 * VA ACÁ Y NO EN EL FORMULARIO porque es un dato de la flota, no del dibujo de
 * la pantalla: el día que una unidad tenga otra, el que lo cambie va a venir a
 * buscar un número, no un `defaultValue` adentro de un JSX.
 *
 * Y ES UNA PROPUESTA, NO UN CANDADO: el campo queda editable. Si mañana entra
 * un equipo distinto, se escribe encima y listo — no hace falta tocar el
 * código para emitir un papel.
 *
 * EN TONELADAS, que es la unidad del formulario («CAPACIDAD DE ARRASTRE (TON)»
 * en el MIC AR.142.067170 del que salieron todos los campos).
 */
export const CAPACIDAD_ARRASTRE_TON = 25;

export const ACOPLADOS = [
  { id: "semirremolque", label: "Semirremolque", pt: "Semi-reboque" },
  { id: "remolque", label: "Remolque", pt: "Reboque" },
];

/* ── QUÉ LLEVA UN MANIFIESTO ───────────────────────────────────────────────
 *
 * Fabián, 21/09: los embarques *consolidados* —un camión con varios CRT, la
 * carátula más hojas de continuación— son ≈40% de lo que emite: «Aprox es el
 * 40% porque trabajamos mucho con las fracciones».
 *
 * Son el eje opuesto al que ya estaba resuelto:
 *
 *   FRACCIONADO   un CRT en varios camiones. Cada camión es un MIC, hoja N de M.
 *   CONSOLIDADO   un camión con varios CRT. Es UN manifiesto, con renglones.
 *
 * `hoja`/`hojas` ya quiere decir «camión N de M», así que los CRT de un
 * manifiesto son RENGLONES (`mic_crt`), que es lo que son en el papel.
 *
 * ── LA REGLA, UNA SOLA ─────────────────────────────────────────────────────
 *
 * **La carga de un MIC son sus renglones; si no tiene renglones, son sus
 * propias columnas.**
 *
 * Así no hay dos formas de anotar el mismo hecho: hay una, con un caso
 * degenerado de un solo CRT que es el que ya venía funcionando. Está escrita
 * igual en la migración `20260921220000`, porque el papel se arma acá y el
 * límite lo cuida la base — y hay tests de los dos lados. */

const RENGLONES = (mic) => {
  const r = (mic?.crts || []).filter(Boolean);
  /* Por `orden`, que es el orden del papel: el 1 es la carátula. Un renglón sin
     orden va al final en vez de adelantarse a la carátula. */
  return [...r].sort((a, b) => (Number(a.orden) || 99) - (Number(b.orden) || 99));
};

export const renglonesDe = RENGLONES;

/* Un manifiesto es consolidado cuando lleva MÁS DE UN CRT. Con uno solo, tenga
 * renglón o no, es el caso de siempre y se lee igual. */
export const esConsolidado = (mic) => RENGLONES(mic).length > 1;

/* ── LO QUE VIENE DEL FORMULARIO NO ES UN NÚMERO A LA ARGENTINA ───────────
 *
 * Fabián, 09/10, en la aduana: «el valor FOB en campo 27 no respeta los
 * decimales declarados (…) debe ser 131.082,00 y dice 1.311.082,00».
 *
 * `aNumero` lee como el papel: el punto es de miles y se borra. Pero lo que
 * tiene el formulario del MIC no viene del papel: viene de un
 * `<input type="number">` o de `String(fobSugerido(…))`, y ahí el punto es
 * DECIMAL. «131108.2» pasaba por `aNumero` y quedaba 1.311.082 — el FOB del
 * renglón 1 de cada consolidado, multiplicado por diez o por cien según los
 * decimales. Un número de verdad o un texto que JavaScript lee entero se
 * toman tal cual; sólo lo que trae coma (algo pegado a mano, «131.082,00»)
 * se lee a la argentina. */
export function deCampo(v) {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const t = String(v ?? "").trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : aNumero(t);
}

const redondear = (n) => Number(n.toFixed(3));

/* Lo que lleva el manifiesto en total, sea de uno o de varios CRT. */
export function cargaDe(mic) {
  const reng = RENGLONES(mic);
  if (!reng.length) {
    // Sin renglones, los valores son los del formulario: ver `deCampo`.
    return {
      bultos: deCampo(mic?.bultos) ?? 0,
      peso_bruto: deCampo(mic?.peso_bruto) ?? 0,
      valor_fot: deCampo(mic?.valor_fot) ?? 0,
    };
  }
  return {
    bultos: redondear(reng.reduce((a, l) => a + (aNumero(l.bultos) ?? 0), 0)),
    peso_bruto: redondear(
      reng.reduce((a, l) => a + (aNumero(l.peso_bruto) ?? 0), 0),
    ),
    /* El campo 27 del manifiesto es la suma del valor de cada carta de porte,
       igual que los bultos (31) y el peso (32): cada renglón trae el suyo. */
    valor_fot: redondear(reng.reduce((a, l) => a + (aNumero(l.valor_fot) ?? 0), 0)),
  };
}

/* Cuánto de ESTE CRT lleva ESTE manifiesto.
 *
 * SIN RENGLONES SE DEVUELVEN LAS COLUMNAS SIN MIRAR EL `crt_id`, y es a
 * propósito: `reparto` se llama con los MIC de un CRT, así que filtrar nada
 * agregaría —y en cambio dejaría en cero a todo MIC al que la pantalla todavía
 * no le pase el id, que es la forma de que una cuenta correcta empiece a decir
 * que falta repartir lo que ya está repartido. */
export function aporteA(mic, crtId) {
  const reng = RENGLONES(mic);
  /* Sólo bultos y peso: el reparto es esa cuenta, el valor FOT (campo 27) no
     entra. `cargaDe` sí lo trae, así que acá se toman sólo las dos columnas. */
  if (!reng.length) {
    const c = cargaDe(mic);
    return { bultos: c.bultos, peso_bruto: c.peso_bruto };
  }
  const mios = crtId === undefined || crtId === null
    ? reng
    : reng.filter((l) => String(l.crt_id) === String(crtId));
  return {
    bultos: redondear(mios.reduce((a, l) => a + (aNumero(l.bultos) ?? 0), 0)),
    peso_bruto: redondear(
      mios.reduce((a, l) => a + (aNumero(l.peso_bruto) ?? 0), 0),
    ),
  };
}

/* El FOB (campo 27 del MIC) que le toca a un camión por REGLA DE TRES sobre el
 * valor total del CRT (campo 14).
 *
 * Fabián, 24/09: cuando un CRT se fracciona en varios camiones, cada MIC declara
 * la PARTE del valor que le corresponde por sus bultos —valor unitario por bulto
 * × bultos de este camión— y esa cuenta la hacía a mano con calculadora. Acá se
 * calcula sola: `valor_monto / bultos` del CRT, por los `bultos` del MIC.
 *
 * Devuelve `null` cuando no se puede (el CRT no tiene valor o bultos, o el MIC
 * todavía no tiene bultos): ahí no se sugiere nada y el campo queda para cargar
 * a mano, sin inventar un número. Redondeo a 2 decimales, como un importe.
 *
 * LOS BULTOS DEL CAMIÓN SE LEEN CON `deCampo`, NO CON `aNumero`. Vienen del
 * formulario (`<input type="number">`, punto decimal) o de la base (número),
 * igual que el FOB del renglón 1 que esta mañana salía ×100. Con `aNumero`,
 * «93.5» eran 935 bultos y el FOB propuesto salía diez veces más grande que el
 * de los 93,5 bultos que después se guardan (`agregarCrt` y `guardar` leen con
 * `Number`). Los del CRT vienen de la base, siempre como número. */
export function fobSugerido(crt, bultosMic) {
  const valor = aNumero(crt?.valor_monto);
  const bultosCrt = aNumero(crt?.bultos);
  const b = deCampo(bultosMic);
  if (!valor || !bultosCrt || bultosCrt <= 0 || b == null) return null;
  return Math.round((valor / bultosCrt) * b * 100) / 100;
}

/* ── El campo 27 de cada carta de porte, en la carátula y en la continuación ─
 *
 * Fabián, 09/10: «fijate de hacer la regla de 3 para el campo 27 de las
 * continuaciones porque no está realizando el cálculo. Se refiere al valor de
 * la mercadería». La carátula ya lo hacía —el formulario propone
 * `fobSugerido` y se puede pisar— pero las cartas que se suman al camión
 * salían con lo que se hubiera tipeado al sumarlas, y sin tipear, en blanco.
 *
 * LO CARGADO MANDA; SI NO HAY NADA CARGADO, LA REGLA DE TRES. Es lo mismo que
 * hace la carátula: el formulario propone la cuenta, la persona puede pisarla,
 * y lo que sale impreso es lo guardado. Pisar con la cuenta algo que alguien
 * escribió a propósito sería borrarle un dato sin avisar; dejar en blanco una
 * casilla que se puede calcular es lo que Fabián reportó.
 *
 * La cuenta es `fobSugerido`, la misma de la carátula: FOB del CRT ÷ bultos
 * del CRT × bultos de esta carta, a dos decimales. No hay otra fórmula.
 *
 * `fobCargado` lee el valor del renglón como viene del formulario o de la
 * base (ver `deCampo`): «131108.2» es 131.108,20 y «131.108,20» también. Vacío
 * o ilegible es «no se cargó» —un cero escrito es un cero, igual que el 28 y
 * el 29 de la continuación—. */
export function fobCargado(carta) {
  const v = carta?.valor_fot;
  if (v === null || v === undefined || String(v).trim() === "") return null;
  return deCampo(v);
}

export const fobDeCarta = (carta, crtRec) =>
  fobCargado(carta) ?? fobSugerido(crtRec, carta?.bultos);

/* En qué renglón entra el próximo CRT. El 1 es la carátula, así que el primero
 * que se agrega la ocupa. Se mira el MÁXIMO y no la cantidad: con un renglón
 * borrado en el medio, contar daría un orden ya usado. */
export function proximoOrden(mic) {
  const usados = RENGLONES(mic)
    .map((l) => Number(l.orden))
    .filter((n) => Number.isFinite(n) && n >= 1);
  return usados.length ? Math.max(...usados) + 1 : 1;
}

/* Si este CRT ya está en el manifiesto. Agregarlo dos veces es contar dos veces
 * la misma mercadería, y la base lo rechaza; decirlo antes evita el cartel. */
export const yaLleva = (mic, crtId) =>
  RENGLONES(mic).some((l) => String(l.crt_id) === String(crtId));

/* ── PASAR DE UNO A VARIOS SIN PERDER LO QUE YA LLEVABA ────────────────────
 *
 * La regla dice que si hay renglones, mandan los renglones. Así que el momento
 * peligroso es el primero: un MIC que declaraba 10 bultos en sus columnas, al
 * recibir su primer CRT de más, pasaría a declarar SÓLO ese — y los 10 se irían
 * sin un cartel, que es la forma de equivocarse que nadie nota.
 *
 * Por eso agregar el primer CRT de más convierte la carga vieja en el renglón
 * 1, que es además lo que es: la carátula. */
export function renglonesAlAgregar(mic, nuevo) {
  const hay = RENGLONES(mic);
  const salida = [];
  if (!hay.length) {
    // Del formulario, no del papel: ver `deCampo`.
    salida.push({
      crt_id: mic?.crt_id,
      bultos: deCampo(mic?.bultos),
      peso_bruto: deCampo(mic?.peso_bruto),
      peso_liquido: deCampo(mic?.peso_liquido),
      valor_fot: deCampo(mic?.valor_fot),
      orden: 1,
    });
  }
  salida.push({ ...nuevo, orden: hay.length ? proximoOrden(mic) : 2 });
  return salida;
}

/* ── Y DESPUÉS, LO QUE SE CORRIGE EN EL 31 VA A SU RENGLÓN ─────────────────
 *
 * Fabián, 14/10, en el AR.142.067598 (dos CRT): corrigió los bultos de la
 * carátula de 396 a 398 y el papel siguió diciendo 396. El 31 de la pantalla
 * es la columna del manifiesto, y con renglones el papel y el reparto leen el
 * renglón (`cargaDe`, `aporteA`): cambió la columna y el renglón no.
 *
 * El renglón del CRT del que cuelga el manifiesto nació de esas columnas
 * (`renglonesAlAgregar`, las mismas cuatro), así que lo que se corrige en el
 * 31, el 32 o el 27 se le corrige a él. SÓLO LO QUE CAMBIÓ desde lo guardado:
 * lo que no se tocó no se copia encima, aunque el renglón diga otra cosa.
 *
 * Lo guarda la base (migración `20261014193100`), comparando lo guardado con
 * lo nuevo. Acá se hace la MISMA cuenta contra `base` —el formulario como se
 * abrió o se guardó por última vez— para que la vista previa, el arrastre y
 * «por qué no entra» digan desde que se escribe lo que va a quedar. */
export const CARGA_EN_SU_RENGLON = ["bultos", "peso_bruto", "peso_liquido", "valor_fot"];

/* El renglón del CRT del que cuelga el manifiesto: uno o ninguno, porque un
   CRT no entra dos veces en el mismo MIC. */
export const renglonPropio = (mic) =>
  RENGLONES(mic).find((l) => String(l.crt_id) === String(mic?.crt_id)) || null;

export function conCargaEnSuRenglon(base, mic) {
  const suyo = renglonPropio(mic);
  if (!suyo) return mic;
  const antes = renglonPropio(base) || suyo;
  const nuevo = { ...suyo };
  for (const k of CARGA_EN_SU_RENGLON) {
    // Del formulario, no del papel: ver `deCampo`.
    nuevo[k] = deCampo(mic[k]) !== deCampo(base?.[k]) ? deCampo(mic[k]) : antes[k];
  }
  return { ...mic, crts: mic.crts.map((l) => (l === suyo ? nuevo : l)) };
}

/* ── EL ARRASTRE ───────────────────────────────────────────────────────────
 *
 * Es lo que Fabián pidió y lo que hoy suma a mano: en la carátula y en cada
 * hoja de continuación, el acumulado de bultos y kilos hasta ese renglón. El
 * último acumulado es el total del manifiesto.
 *
 * Sumar a mano una columna de tres o cuatro renglones no falla casi nunca, y
 * ese «casi» se descubre en la frontera. */
export function arrastre(mic) {
  let bultos = 0;
  let peso = 0;
  const renglones = RENGLONES(mic).map((l, i) => {
    bultos = redondear(bultos + (aNumero(l.bultos) ?? 0));
    peso = redondear(peso + (aNumero(l.peso_bruto) ?? 0));
    return {
      /* El id del renglón viaja con el arrastre porque es lo que la pantalla
         necesita para sacarlo. Buscarlo aparte por `crt_id` sería tener dos
         formas de identificar la misma fila. */
      id: l.id,
      orden: Number(l.orden) || i + 1,
      crt_id: l.crt_id,
      bultos: aNumero(l.bultos) ?? 0,
      peso_bruto: aNumero(l.peso_bruto) ?? 0,
      bultos_acumulados: bultos,
      peso_acumulado: peso,
    };
  });
  return { renglones, bultos, peso_bruto: peso, hojas: renglones.length };
}

/* ── Paginar el manifiesto en hojas, como va impreso ──────────────────────
 *
 * Del modelo real de Fabián (mic67413 = carátula, micc67413 = continuación,
 * 22/09):
 *
 *   CARÁTULA (hoja 1)  lleva UNA carta de porte (la del renglón `orden` 1).
 *   CONTINUACIÓN       las demás, de a DOS por hoja. Repite el encabezado, dice
 *                      «Continuación», el mismo Nº de MIC, y al pie el acumulado
 *                      (casilleros 42-47): SUBTOTALES de la hoja, TOTALES de la
 *                      HOJA ANTERIOR y TOTALES ACUMULADOS.
 *
 * Total de hojas M = 1 + ceil((N-1)/2), con N = cartas de porte. El acumulado
 * sale del `arrastre`, que ya lleva la suma corrida por renglón.
 *
 * Un MIC de una sola carta (el fraccionado, y el caso de siempre) es UNA hoja y
 * no tiene continuación: sale igual que hoy.
 *
 * ── LA HOJA QUE SE CIERRA CON UN CASILLERO VACÍO (Fabián, 02/10) ────────────
 *
 * Carga en dos depósitos, mismo camión: en el depósito 1 la carátula y una
 * continuación con UNA carta; en el depósito 2 otra continuación. Son dos
 * aduanas con dos firmas, así que la hoja del depósito 1 se cierra aunque le
 * sobre un casillero, y la del depósito 2 abre hoja nueva. El sistema que usan
 * hoy lo resuelve con un «CRT 0» sin datos que ocupa el casillero.
 *
 * Acá es una marca en el renglón: `cierra_hoja`. Un renglón marcado que abre
 * una hoja de continuación va SOLO en ella —el otro casillero sale vacío, con
 * el número 0— y el siguiente abre la hoja de al lado. Vale aunque sea la
 * última por ahora: en el depósito 1 se imprime con el 0 antes de que exista
 * la carta del depósito 2. No suma nada: el
 * arrastre de bultos y kilos no se entera. La marca en el segundo casillero de
 * una hoja no cambia nada: esa hoja ya está llena.
 */
export function paginarHojas(mic) {
  const reng = RENGLONES(mic);
  const arr = arrastre(mic).renglones; // suma corrida por renglón, en `orden`
  if (reng.length <= 1) {
    return [{ tipo: "caratula", hoja: 1, hojas: 1, cartas: reng }];
  }
  const grupos = [];
  const cerradas = new Set();
  for (let i = 1; i < reng.length;) {
    if (reng[i].cierra_hoja) {
      cerradas.add(grupos.length);
      grupos.push([reng[i]]);
      i += 1;
    } else {
      grupos.push(reng.slice(i, i + 2));
      i += 2;
    }
  }
  const hojas = grupos.length + 1;
  const salida = [{ tipo: "caratula", hoja: 1, hojas, cartas: [reng[0]] }];
  let desde = 1; // índice en `arr` donde arranca esta hoja
  grupos.forEach((g, k) => {
    const hasta = desde + g.length - 1;
    const antB = arr[desde - 1]?.bultos_acumulados ?? 0;
    const antP = arr[desde - 1]?.peso_acumulado ?? 0;
    const acuB = arr[hasta]?.bultos_acumulados ?? 0;
    const acuP = arr[hasta]?.peso_acumulado ?? 0;
    salida.push({
      tipo: "continuacion",
      hoja: k + 2,
      hojas,
      cartas: g,
      /* Cerrada a propósito con un casillero vacío («CRT 0»), no la última
         hoja a la que le sobró uno. */
      cerrada: cerradas.has(k),
      subtotal: { bultos: redondear(acuB - antB), peso_bruto: redondear(acuP - antP) },
      anterior: { bultos: antB, peso_bruto: antP },
      acumulado: { bultos: acuB, peso_bruto: acuP },
    });
    desde = hasta + 1;
  });
  return salida;
}

/* ── El reparto ────────────────────────────────────────────────────────────
 *
 * Un MIC anulado NO ocupa lugar: por eso se puede rehacer uno sin que el
 * tránsito quede sobre-manifestado. Es la misma regla que aplica el trigger.
 */
const VIVOS = (mics) => (mics || []).filter((m) => m.estado !== "anulado");

function tramo(total, repartido) {
  const t = aNumero(total);
  const r = repartido;
  if (t === null) {
    // Sin total declarado no se puede decir que falte ni que sobre.
    return { total: null, repartido: r, falta: null, sobra: null, cierra: false };
  }
  const dif = Number((t - r).toFixed(3));
  return {
    total: t,
    repartido: r,
    falta: dif > 0 ? dif : 0,
    sobra: dif < 0 ? -dif : 0,
    cierra: dif === 0,
  };
}

export function reparto(crt, mics) {
  const vivos = VIVOS(mics);
  /* POR CRT, no por manifiesto. Un consolidado lleva varios CRT, y para ESTE
     tránsito sólo aporta su renglón: sumarle todo lo que lleva el camión diría
     que se pasó por llevar mercadería de otra carta de porte. */
  const aportes = vivos.map((m) => aporteA(m, crt?.id));
  const sumar = (campo) =>
    Number(aportes.reduce((a, x) => a + (x[campo] ?? 0), 0).toFixed(3));

  const bultos = tramo(crt?.bultos, sumar("bultos"));
  const peso = tramo(crt?.peso_bruto, sumar("peso_bruto"));
  const camiones = Number(crt?.camiones) || null;

  return {
    bultos,
    peso,
    hojas: {
      usadas: vivos.length,
      total: camiones,
      libres: camiones ? Math.max(0, camiones - vivos.length) : null,
    },
    /* Cierra cuando cierran LAS DOS cuentas y están todas las hojas. Que
       coincidan los bultos con un camión de menos no es un tránsito repartido:
       es uno al que le falta una hoja.

       SIN CAMIONES DECLARADOS (Fabián, 05/10: «no tenemos 100% de seguridad en
       la cantidad») no hay hojas que contar: cierra cuando cierran bultos y
       kilos, que es lo único que se sabe. */
    cierra:
      bultos.cierra &&
      peso.cierra &&
      (!camiones || vivos.length === camiones),
  };
}

/* ¿VA EN «MIC CERRADOS»? (Fabián, 15/10, #424: «separar los embarques que ya
 * están cerrados de los que están en curso»).
 *
 * - «a mano»: quien carga lo dio por terminado (`crt_embarque_cerrado`). Manda
 *   siempre: los manifestados con el otro sistema o con los kilos redondeados
 *   no cierran nunca por la cuenta.
 * - «solo»: la cuenta cierra (`reparto`) y no le queda ningún borrador. Un
 *   borrador es un camión que todavía no salió, aunque los números den.
 * - null: en curso. */
export function embarqueCerrado(crt, mics, aMano) {
  if (aMano) return "a mano";
  const vivos = VIVOS(mics);
  if (!vivos.length || vivos.some((m) => m.estado === "borrador")) return null;
  return reparto(crt, mics).cierra ? "solo" : null;
}

/* Cómo se lee el reparto en una línea. */
export function comoVaElReparto(crt, mics) {
  const r = reparto(crt, mics);
  if (r.bultos.total === null) return "El CRT todavía no declara cuántos bultos son.";
  if (r.cierra) return "La cuenta cierra.";

  const partes = [];
  if (r.bultos.falta) partes.push(`${comoEntero(r.bultos.falta)} bultos`);
  if (r.peso.falta) partes.push(`${comoPeso(r.peso.falta)} kg`);

  if (partes.length) {
    const hojas =
      r.hojas.libres !== null && r.hojas.libres > 0
        ? ` en ${r.hojas.libres} ${r.hojas.libres === 1 ? "camión" : "camiones"}`
        : "";
    return `Falta repartir ${partes.join(" y ")}${hojas}.`;
  }

  if (r.hojas.libres) {
    return `Los totales ya cierran, pero faltan ${r.hojas.libres} de los ${r.hojas.total} camiones.`;
  }
  return "";
}

/* LO QUE QUEDA DE UN CRT PARA ESTE CAMIÓN (Fabián, 27/10, ticket 533: «al
 * declarar bultos y pesos que me vaya mostrando lo disponible en los campos
 * 31 y 32 como cuando hago MIC con un solo CRT»).
 *
 * `mics` son los MIC de ESE CRT tal como los arma `porCrt`: los suyos y los
 * consolidados que lo llevan en un renglón. `reparto` ya cuenta de cada uno
 * sólo lo de este CRT (`aporteA`), así que una carta que va en el camión de
 * otro consolidado se descuenta, y lo que ese camión lleva de otros CRT no.
 *
 * EL MANIFIESTO QUE SE ESTÁ EDITANDO SE SACA POR SU ID: lo suyo es `carga`,
 * lo que dice la pantalla ahora. Contarlo también desde la base lo restaría
 * dos veces.
 *
 * `disponible` es lo que dejan los demás; `quedan`, lo que sobra después de
 * `carga` (negativo si se pasa), o null si todavía no se escribió nada. Sin
 * total declarado en el CRT, null: no se puede decir cuánto queda. */
export function saldoDelCrt(crt, mics, mic, carga) {
  if (!crt) return null;
  const otros = (mics || []).filter(
    (m) => mic?.id == null || String(m.id) !== String(mic.id),
  );
  const r = reparto(crt, otros);
  const resta = (t, usado) =>
    t.total === null || usado === null ? null : redondear(t.total - t.repartido - usado);
  const bultos = deCampo(carga?.bultos);
  const peso = deCampo(carga?.peso_bruto);
  return {
    disponible: { bultos: resta(r.bultos, 0), peso: resta(r.peso, 0) },
    quedan: { bultos: resta(r.bultos, bultos), peso: resta(r.peso, peso) },
  };
}

/* ── Las hojas ─────────────────────────────────────────────────────────────
 *
 * La M de «N de M» la declara el CRT, SI LA SABE. Un MIC anulado libera su
 * hoja: el que se rehace ocupa el mismo lugar en vez de dejar un «2 de 3» que
 * no existe.
 *
 * DECLARARLA ES OPCIONAL (Fabián, 05/10: «necesitamos declarar la cantidad de
 * camiones que vamos a utilizar, no tenemos 100% de seguridad en la cantidad,
 * ¿será posible eliminar eso?»). Sin M, la única hoja libre es la próxima: el
 * primer número que no tiene nadie de ESTE CRT. Es lo mismo que hace
 * `mic_emitir` en la base. */
const PROPIOS = (crt, mics) =>
  VIVOS(mics).filter(
    (m) => crt?.id == null || m.crt_id == null || String(m.crt_id) === String(crt.id),
  );

export function hojasLibres(crt, mics) {
  const total = Number(crt?.camiones) || 0;
  if (!total) {
    const suyas = new Set(
      PROPIOS(crt, mics).map((m) => Number(m.hoja)).filter((n) => n >= 1),
    );
    let n = 1;
    while (suyas.has(n)) n += 1;
    return [n];
  }
  const tomadas = new Set(
    VIVOS(mics).map((m) => Number(m.hoja)).filter((n) => n >= 1),
  );
  const libres = [];
  for (let n = 1; n <= total; n += 1) if (!tomadas.has(n)) libres.push(n);
  return libres;
}

/* La M de un tránsito. Declarada, es `crt.camiones`. Sin declarar, son los
 * MIC EMITIDOS de este CRT hasta ahora —crece sola con cada camión—, y nunca
 * menos que la hoja más alta: un anulado del medio no puede hacer decir «3 de
 * 2» a la última. */
export function totalDeHojas(crt, mics) {
  const declarado = Number(crt?.camiones) || null;
  if (declarado) return declarado;
  const emitidos = PROPIOS(crt, mics).filter((m) => m.estado === "emitido");
  const mayor = emitidos.reduce((a, m) => Math.max(a, Number(m.hoja) || 0), 0);
  return Math.max(emitidos.length, mayor) || null;
}

/* La M que dice ESTE MIC. La congelada al emitir si la hay (`mic.hojas`, que
 * sólo existe cuando el CRT la declaró); si no, la del tránsito en vivo. Un
 * borrador se cuenta como si ya estuviera emitido: lo que se muestra es lo
 * que va a decir la hoja cuando salga. */
export function hojasDe(mic, crt, mics) {
  if (Number(mic?.hojas)) return Number(mic.hojas);
  const conEste = mic?.estado === "borrador"
    ? [
        ...(mics || []).filter((m) => String(m.id) !== String(mic.id)),
        { ...mic, estado: "emitido" },
      ]
    : mics;
  return totalDeHojas(crt, conEste);
}

export const proximaHoja = (crt, mics) => hojasLibres(crt, mics)[0] ?? null;

/* Un reparto parejo, para PROPONER. El resto va en las primeras hojas: repartir
 * 10 bultos en 3 camiones da 4-3-3, no 3,33 cada uno. Un bulto no se parte. */
export function repartirParejo(total, camiones) {
  const t = aNumero(total);
  const n = Number(camiones);
  if (t === null || !Number.isFinite(n) || n < 1) return [];
  const base = Math.floor(t / n);
  const resto = Math.round((t - base * n) * 1000) / 1000;
  const salida = new Array(n).fill(base);
  // El resto entero se reparte de a uno; lo que quede con decimales, a la primera.
  let sobra = resto;
  for (let i = 0; i < n && sobra >= 1; i += 1) { salida[i] += 1; sobra -= 1; }
  if (sobra > 0) salida[0] = Number((salida[0] + sobra).toFixed(3));
  return salida;
}

/* ── Qué falta antes de emitir ─────────────────────────────────────────────
 *
 * Lo exigido es lo que el manifiesto no puede salir sin decir: qué camión, con
 * qué chofer, cuánto lleva y de qué CRT cuelga. El camión sustituto, el seguro
 * y los códigos de aduana salen en blanco en manifiestos reales. */
export const PARA_EMITIR = [
  { campo: "numero", label: "el número" },
  { campo: "crt_id", label: "de qué CRT cuelga" },
  { campo: "placa", label: "la placa del camión" },
  { campo: "acoplado_placa", label: "la placa del acoplado" },
  { campo: "motorista", label: "el motorista" },
  { campo: "motorista_dni", label: "el DNI del motorista" },
  { campo: "bultos", label: "la cantidad de bultos" },
  { campo: "peso_bruto", label: "el peso bruto" },
  { campo: "descripcion", label: "la descripción de la mercadería" },
];

/* ── El talonario sin confirmar ────────────────────────────────────────────
 *
 * El de MIC arranca en AR.142.068000, un bloque que elegimos nosotros porque no
 * sabíamos el último número en papel. Los cuatro de CRT sí están empalmados.
 *
 * Fabián, 16/09: *«ponerles cualquier numero y al momento que funcione al 100,
 * te paso la numeración correcta»*. O sea que el estado provisorio es
 * deliberado y va a durar un tiempo.
 *
 * BORRADORES SÍ, EMITIR NO. Un borrador con un número inventado es exactamente
 * lo que pidió y no le hace mal a nadie: vive adentro del hub y se corrige. Un
 * MIC EMITIDO es un papel que se presenta en la frontera, y emitido no se
 * edita —lo congela un trigger—, así que un número inventado ahí adentro queda
 * para siempre y encima rompe la correlatividad que todo esto vino a cuidar.
 *
 * Por eso la serie sin confirmar no frena nada del trabajo de todos los días y
 * frena la única cosa que no se puede deshacer. */
export const SERIE_SIN_CONFIRMAR =
  "La numeración del talonario de MIC todavía no está empalmada con el papel: "
  + "el número es provisorio. Se pueden hacer borradores; para emitir hace "
  + "falta la numeración real.";

export const serieProvisoria = (serie) => !!serie && serie.confirmada === false;

/* ── El número tiene la forma del talonario, o se confirma ─────────────────
 *
 * Fabián, 13/10: «tipear lo menos posible y evitar el error de tipeo». Ese
 * mismo día salió emitido un MIC de Transcan con «ARR.TCC000093» entre el
 * AR.TCC.000092 y el AR.TCC.000095, y emitido no se edita: queda así.
 *
 * Si la empresa tiene talonario de MIC, el número es su prefijo y exactamente
 * `ancho` cifras. Lo que no lo es NO SE PROHÍBE —hay números legítimos fuera
 * del talonario, y el número lo decide quien emite—, pero no se emite sin que
 * alguien lo haya confirmado mirándolo. Sin talonario no hay forma contra la
 * que comparar, y no se inventa un problema.
 *
 * Devuelve la frase para mostrar al lado del número, o "" si está bien. La
 * misma regla la aplica `mic_emitir` en la base, para la pestaña vieja. */
export function numeroFueraDeSerie(numero, serie) {
  if (!serie || !serie.prefijo) return "";
  const t = String(numero ?? "").trim();
  if (!t) return ""; // Eso lo dice PARA_EMITIR: «Falta el número».
  const ancho = Number(serie.ancho || 3);
  const resto = t.startsWith(serie.prefijo) ? t.slice(serie.prefijo.length) : null;
  if (resto !== null && resto.length === ancho && /^\d+$/.test(resto)) return "";
  const ejemplo = proximoNumero(serie);
  return `El número ${t} no tiene la forma del talonario de MIC de esta empresa: `
    + `${serie.prefijo} y ${ancho} cifras${ejemplo ? `, como ${ejemplo}` : ""}.`;
}

/* La confirmación es de UN número: si se lo cambia, hay que volver a mirarlo.
   Por eso viaja el número confirmado y no un sí suelto. */
export function numeroConfirmadoVale(numero, confirmado) {
  const t = String(numero ?? "").trim();
  return !!t && t === String(confirmado ?? "").trim();
}

export function queFaltaParaEmitir(mic, serie, { numeroConfirmado = "" } = {}) {
  /* VA PRIMERO Y SOLO. Listarlo junto a «falta el motorista» lo haría leer
     como un campo más para completar, y no es algo que el que está en la
     pantalla pueda resolver: lo resuelve Fabián pasando el número. */
  if (serieProvisoria(serie)) return SERIE_SIN_CONFIRMAR;

  const falta = PARA_EMITIR.filter((c) => {
    const v = mic?.[c.campo];
    if (v === null || v === undefined) return true;
    return String(v).trim() === "";
  }).map((c) => c.label);

  if (falta.length === 1) return `Falta ${falta[0]}.`;
  if (falta.length > 1) {
    return `Faltan ${falta.slice(0, -1).join(", ")} y ${falta[falta.length - 1]}.`;
  }

  /* Con todo completo, lo último es el número. Va después de lo que falta
     porque el aviso ya está al lado del número desde que se lo escribe: acá
     sólo explica por qué el botón no se habilita. */
  const raro = numeroFueraDeSerie(mic?.numero, serie);
  if (raro && !numeroConfirmadoVale(mic?.numero, numeroConfirmado)) {
    return `${raro} Si es correcto, confirmalo al lado del número.`;
  }
  return "";
}

/* ── Y por qué no entra ────────────────────────────────────────────────────
 *
 * Espejo de lo que hace el trigger, para decirlo ANTES y con números. Que la
 * base sea la que manda no quita que la pantalla tenga que explicarlo: un
 * `check_violation` de Postgres no le dice nada a nadie. */
export function porQueNoEntra(mic, crt, otros) {
  if (!crt) return "";
  const resto = (otros || []).filter(
    (m) => String(m.id) !== String(mic?.id) && m.estado !== "anulado",
  );
  const conEste = [...resto, { ...mic, estado: "borrador" }];
  const r = reparto(crt, conEste);

  if (r.bultos.sobra) {
    return `Con este MIC los bultos suman ${comoEntero(
      r.bultos.repartido,
    )} y el CRT declara ${comoEntero(r.bultos.total)}: sobran ${comoEntero(
      r.bultos.sobra,
    )}.`;
  }
  if (r.peso.sobra) {
    return `Con este MIC el peso suma ${comoPeso(
      r.peso.repartido,
    )} kg y el CRT declara ${comoPeso(r.peso.total)}: sobran ${comoPeso(
      r.peso.sobra,
    )} kg.`;
  }
  if (crt.estado !== "emitido") {
    return `El CRT ${crt.numero} todavía no está emitido: no se puede manifestar un tránsito que no salió.`;
  }
  /* Sin camiones declarados se emite igual; lo único que no puede pasar es
     que la hoja salte números. Mismo chequeo que `mic_emitir`. */
  if (!crt.camiones && Number(mic?.hoja) > 0) {
    const conHoja = PROPIOS(crt, resto).filter((m) => Number(m.hoja) >= 1).length;
    if (Number(mic.hoja) > conHoja + 1) {
      return `El camión ${mic.hoja} salta números: el que sigue es el ${conHoja + 1}.`;
    }
  }
  if (crt.camiones && !mic?.hoja && hojasLibres(crt, resto).length === 0) {
    return `El CRT ${crt.numero} ya tiene sus ${crt.camiones} camiones.`;
  }
  return "";
}

/* ── Cómo se listan ────────────────────────────────────────────────────────
 * Por hoja, que es el orden del legajo. Los que todavía no tienen hoja van al
 * final: son los que faltan emitir. */
export function ordenar(mics) {
  return [...(mics || [])].sort((a, b) => {
    const ha = Number(a.hoja) || 99;
    const hb = Number(b.hoja) || 99;
    if (ha !== hb) return ha - hb;
    return String(a.creado_en || "").localeCompare(String(b.creado_en || ""));
  });
}

/* ── El campo 5, «Hoja / Folha» ────────────────────────────────────────────
 *
 * Es la hoja FÍSICA de ESTE manifiesto: la carátula es la 1; si el manifiesto
 * se continúa, la 2, la 3… El total es cuántas hojas ocupa ESE MIC.
 *
 * NO es el «camión N de M» del reparto. Cuál camión del CRT es un MIC lo llevan
 * las columnas `hoja`/`hojas`, para el legajo y el control del reparto —y ahí
 * siguen queriendo decir eso, ver `comoSeLee` acá abajo—, pero el casillero 5
 * del papel pide otra cosa. Imprimir esas columnas ahí hacía que un CRT
 * fraccionado en 3 camiones saliera 1/3, 2/3, 3/3, cuando cada camión es su
 * propio manifiesto de una sola hoja. Fabián, 22/09/2026: *«los MIC que llevan
 * solo una carta de porte siempre en el campo 5 debe decir 1 / 1»*.
 *
 * Un MIC de una sola carta de porte (el fraccionado, y el caso de siempre) es
 * una sola hoja: «1 / 1». Un consolidado se pagina —la carátula más una hoja de
 * continuación cada dos cartas de más—, y ahí la carátula es «1 / M» y cada
 * continuación «k / M». El total M lo da `paginarHojas`. */
export function campoHoja(mic) {
  /* La carátula es la hoja 1; el total sale de la paginación (1 sola si es una
     carta de porte; más si el consolidado se continúa). */
  const hoja = 1;
  const hojas = paginarHojas(mic).length;
  return { hoja, hojas, texto: `${hoja} / ${hojas}` };
}

/* Las partes (remitente/destinatario/consignatario) como van en las casillas
 * 33, 34 y 35 del MIC: TRES renglones y nada más.
 *
 * Fabián, 25/09/2026: «En los campos 33,34,35 podes resumir a 3 renglones:
 * razón social (lo más importante, no se puede resumir) / domicilio / provincia
 * y país. El CUIT no es necesario, se puede quitar».
 *
 * Es propio del MIC, NO se toca `comoBloque` de `crt.js`: ése lo comparte con el
 * CRT y de CRT ya hay emitidos en la calle —un papel presentado no cambia de
 * forma—. La tercera línea junta localidad, provincia y país en una sola, que es
 * el «dónde» que pidió, y el CUIT queda afuera a propósito. */
export function comoBloqueMic(parte) {
  if (!parte) return [];
  const donde = [parte.localidad, parte.provincia, parte.pais]
    .map((x) => String(x ?? "").trim())
    .filter(Boolean)
    .join(" - ");
  return [
    String(parte.razon_social ?? "").trim(),
    String(parte.domicilio ?? "").trim(),
    donde,
  ].filter(Boolean);
}

export function comoSeLee(mic) {
  if (!mic) return "";
  const hoja =
    mic.hoja && mic.hojas ? `camión ${mic.hoja}/${mic.hojas}` : "sin camión";
  /* LA CARGA SALE DE LA MISMA REGLA QUE TODO LO DEMÁS. Leyendo `mic.bultos` a
     secas, un consolidado mostraba lo que tuviera esa columna —o nada— en vez
     de lo que realmente lleva. */
  const c = cargaDe(mic);
  const carga = [
    c.bultos ? `${comoEntero(c.bultos)} ${mic.bultos_tipo || "bultos"}` : "",
    c.peso_bruto ? `${comoPeso(c.peso_bruto)} kg` : "",
  ].filter(Boolean).join(" · ");
  /* Cuántos CRT lleva sólo se dice cuando son varios: «1 CRT» en cada línea de
     una lista de tránsitos fraccionados es ruido en todos los renglones. */
  const cuantos = esConsolidado(mic)
    ? `${RENGLONES(mic).length} CRT`
    : "";
  const camion = [mic.placa, mic.acoplado_placa].filter(Boolean).join(" + ");
  return [hoja, camion, cuantos, carga].filter(Boolean).join(" — ");
}

/* ── La continuación, con campos declarados a mano ─────────────────────────
 *
 * Fabián, 01/10: «en la hoja continuación necesitamos declarar de manera
 * manual los campos 24, 25, 26, 28, 29, 30, 37, 38 y 40». Cada renglón
 * (`mic_crt`) puede traer los suyos, y VACÍO ES «LO DE SIEMPRE»: el 24, 25,
 * 26 y 30 del manifiesto, el 37 y el 38 de su CRT, el 28 y el 29 en blanco
 * (son del manifiesto y ya están en la carátula). Así todo lo cargado antes de
 * este cambio sale igual.
 *
 * El 40 es de la hoja: se guarda en la PRIMERA carta de cada hoja —la que la
 * abre—, ver `campo40DeHoja`. */
export const MANUALES_CONTINUACION = [
  { k: "aduana_destino", n: "24", label: "Aduana de destino" },
  { k: "aduana_destino_codigo", n: "24", label: "Código", corto: true },
  { k: "moneda", n: "25", label: "Moneda" },
  { k: "origen_mercaderias", n: "26", label: "Origen de las mercancías" },
  { k: "origen_codigo", n: "26", label: "Código", corto: true },
  { k: "flete", n: "28", label: "Flete en US$", numero: true },
  { k: "seguro", n: "29", label: "Seguro en US$", numero: true },
  { k: "bultos_tipo", n: "30", label: "Tipo de bultos" },
  { k: "bultos_tipo_codigo", n: "30", label: "Código", corto: true },
  { k: "precintos", n: "37", label: "Número de los precintos" },
  { k: "descripcion", n: "38", label: "Marcas, números y descripción", largo: true },
];

const declarado = (v) =>
  v !== null && v !== undefined && String(v).trim() !== "";

/* Lo que imprime una carta de porte de la continuación, campo por campo. Pura.
 * `crtRec` es su CRT (de ahí salen el 37 y el 38 cuando no se declararon). */
export function cartaContinuacion(mic, carta, crtRec) {
  const m = mic || {};
  const c = carta || {};
  const doc = crtRec || {};
  const o = (k, porDefecto) => (declarado(c[k]) ? c[k] : porDefecto);
  return {
    aduana_destino: o("aduana_destino", m.aduana_destino),
    aduana_destino_codigo: o("aduana_destino_codigo", m.aduana_destino_codigo),
    moneda: o("moneda", m.moneda),
    origen_mercaderias: o("origen_mercaderias", m.origen_mercaderias),
    origen_codigo: o("origen_codigo", m.origen_codigo),
    flete: declarado(c.flete) ? aNumero(c.flete) : null,
    seguro: declarado(c.seguro) ? aNumero(c.seguro) : null,
    bultos_tipo: o("bultos_tipo", m.bultos_tipo),
    bultos_tipo_codigo: o("bultos_tipo_codigo", m.bultos_tipo_codigo),
    precintos: o("precintos", doc.precintos),
    /* Ojo: el CRT no tiene ni 37 ni 38 propios, así que en la base esto cae
       en blanco. Desde el 14/10 la carta que se suma al camión nace con su
       38 escrito desde el 11 de su CRT (`descripcionDelCrt`, en `sumarCrt`)
       y se edita en la tarjeta de la continuación. No se deduce acá, al
       imprimir: un emitido reimpreso tiene que decir lo mismo que el día que
       salió. */
    descripcion: o("descripcion", doc.descripcion),
  };
}

/* El 40 declarado para una hoja de continuación, o `null` si va el del
 * manifiesto. Se lee de la carta que abre la hoja; si ésa no lo tiene y la
 * segunda sí, vale el de la segunda —un renglón sacado corre las cartas de
 * hoja, y perder lo escrito por eso sería peor que leerlo de la vecina—. */
export function campo40DeHoja(hoja) {
  const c = (hoja?.cartas || []).find((x) => declarado(x?.campo40));
  return c ? String(c.campo40) : null;
}

/* ── El 38 sale del 11 del CRT ─────────────────────────────────────────────
 *
 * Fabián, 14/10: «el campo 38 del MIC viene en blanco, lo estoy cargando
 * manual. Fijate de arrastrarlo del Campo 11 del CRT». El 38 («Marcas y
 * números de los bultos, descripción de las mercancías») y el 11 del CRT
 * («Cantidad y clase de bultos, marcas y números, tipo de mercancías,
 * contenedores y accesorios») dicen lo mismo, y lo pasaba a mano camión por
 * camión.
 *
 * NO ES EL 11 LETRA POR LETRA, Y ES A PROPÓSITO. Los 111 MIC emitidos hasta
 * el 14/10 tienen el 38 escrito por él, y NINGUNO lleva la cantidad del CRT:
 * desde el 29/09 todos arrancan con la DEL CAMIÓN —«691 BULTOS CONTENIENDO
 * PARTES Y PIEZAS…» en un CRT de 5.312—; antes, algunos salían sin cantidad,
 * y el único que no coincide con su 31 es un tipeo (el AR.142.067457 lleva 68
 * bultos y su 38 dice 88, que es justo lo que esto evita). Copiar el 11 con
 * el total del CRT pondría en cada hoja de un fraccionado una cantidad que
 * contradice su propio 31. Es el renglón del 11 tal como lo imprime
 * `CrtPapel` —«N CLASE CONTENIENDO mercadería»— con la N de este camión.
 *
 * LOS CONTENEDORES, SÓLO SI ES UNO. En contenedor completo cada camión lleva
 * uno, y el 38 es donde se dice cuál (ver `shared/micContenedores`). Pasar los
 * siete del 11 a cada MIC haría que cada contenedor figure en siete MIC: los
 * avisos del 13/10 dirían «repetido» en todos y «sin MIC» en ninguno. Con un
 * solo contenedor en el CRT no hay duda y va en su renglón, como lo escribe
 * él; con varios sale la mercadería sin números —también los escritos a mano
 * en ella— y el aviso de debajo del 38 dice cuáles faltan.
 *
 * LA REFERENCIA DEL 11 NO VIENE: es el número del tránsito, y en el MIC va en
 * el 36, que ya se copia del CRT al armarlo.
 *
 * Lo que la mercadería ya trae adelante —«CONTENIENDO…», «QUE DICEN
 * CONTENER…», «BULTOS CONTENIENDO…», hasta «19 BULTOS…»— no se repite. El
 * CRT lo imprime dos veces («2059 BULTOS CONTENIENDO CONTENIENDO…») y él no
 * lo pasaba así: el AR.142.067595 dice «928 BULTOS CONTENIENDO…» de un CRT
 * cuya mercadería arranca con «BULTOS CONTENIENDO». Sin bultos todavía sale
 * la mercadería sola: una N inventada sería peor que ninguna. */
const DICE_QUE_CONTIENE = /^(CONTENIENDO|QUE\s+DICEN?\s+CONTENER)\b/i;
const escapar = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export function descripcionDelCrt(crt, bultos) {
  if (!crt) return "";
  const clase = String(crt.bultos_clase ?? "").trim() || "BULTOS";
  let merc = String(crt.mercaderia ?? "").trim();
  if (vaEnContenedores(crt)) merc = sinContenedores(merc).trim();
  const n = deCampo(bultos);
  let renglon = merc;
  if (n !== null) {
    const cuantos = Number.isInteger(n) ? comoEntero(n) : comoMiles(n, 2);
    const c = escapar(clase);
    // La cantidad que traía el texto es la del CRT, no la de este camión.
    const resto = merc.replace(new RegExp(`^[\\d.,]+\\s+(?=${c}(ES|S)?\\b)`, "i"), "");
    if (!resto) renglon = `${cuantos} ${clase}`;
    else if (new RegExp(`^${c}(ES|S)?\\b`, "i").test(resto)) renglon = `${cuantos} ${resto}`;
    else if (DICE_QUE_CONTIENE.test(resto)) renglon = `${cuantos} ${clase} ${resto}`;
    else renglon = `${cuantos} ${clase} CONTENIENDO ${resto}`;
  }
  return [renglon, contenedorUnico(crt)].filter(Boolean).join("\n");
}

/* Los bultos de la carta de la carátula: los del renglón 1 si el manifiesto
 * ya tiene renglones —el papel lee el renglón, ver `cargaDe`—, si no el 31. */
const bultosDeLaCaratula = (mic) => {
  const reng = RENGLONES(mic);
  return reng.length ? reng[0].bultos : mic?.bultos;
};

/* El 38 que se le propondría a este MIC ahora, o "" si no corresponde. */
export const propuesta38 = (mic, crt) =>
  crt && (crt.id == null || mic?.crt_id == null || String(crt.id) === String(mic.crt_id))
    ? descripcionDelCrt(crt, bultosDeLaCaratula(mic))
    : "";

const enBorrador = (mic) => !mic?.estado || mic.estado === "borrador";

/* El MIC con el 38 propuesto, SÓLO SI ESTÁ VACÍO Y ES BORRADOR. Lo escrito no
 * se pisa —lo automático llena huecos, la persona corrige—, y un emitido no
 * cambia solo: es un papel que ya se presentó, y lo que se le corrige lo
 * escribe una persona. */
export function con38DelCrt(mic, crt) {
  if (!mic || !enBorrador(mic) || declarado(mic.descripcion)) return mic;
  const d = propuesta38(mic, crt);
  return d ? { ...mic, descripcion: d } : mic;
}

/* Cambiar los bultos (31) cambia la N del 38, PERO SÓLO SI EL 38 SIGUE
 * DICIENDO LO PROPUESTO. Si alguien lo tocó, ya es suyo y se deja. Es la
 * regla de `siguiendoAlValor` en el CRT (la 16 sigue a la 14). Con renglones
 * no arrastra nada: el 31 mueve el renglón de la carátula
 * (`conCargaEnSuRenglon`), pero el 38 de un consolidado se deja como está. */
export function siguiendoLosBultos(mic, crt, bultos) {
  const sig = { ...mic, bultos };
  if (!enBorrador(mic) || RENGLONES(mic).length) return sig;
  const antes = propuesta38(mic, crt);
  if (String(mic?.descripcion ?? "").trim() !== antes) return sig;
  return { ...sig, descripcion: propuesta38(sig, crt) };
}

/* ── El 24, el 26 y el 40, de una lista ────────────────────────────────────
 *
 * Fabián, 13/10: «viendo que se puede modificar para tipear lo menos posible
 * y evitar el error de tipeo». La aduana de destino estaba escrita de nueve
 * formas en los MIC emitidos —«JOW KON», «JOW JON LOGISTICA SRL», el código
 * 110B por 1100B— y la ruta, un renglón de 150 letras, de doce.
 *
 * LAS OPCIONES ESTÁN EN LA BASE (`mic_opcion`), no acá: sumar una aduana o una
 * ruta no pide publicar. Cada opción llena la columna del campo y, si tiene,
 * la de su código, que es lo que el papel imprime en los cuadraditos.
 *
 * ELEGIR NO TRABA: los campos de texto siguen ahí, y lo que no está en la
 * lista se escribe a mano como siempre. */
export const CAMPOS_CON_LISTA = {
  aduana_destino: { codigo: "aduana_destino_codigo", n: "24", label: "Aduana de destino" },
  origen_mercaderias: { codigo: "origen_codigo", n: "26", label: "Origen de las mercancías" },
  ruta: { codigo: null, n: "40", label: "Ruta" },
};

/* Las filas de `mic_opcion`, por campo y en el orden de la lista. */
export function opcionesPorCampo(filas) {
  const porCampo = {};
  for (const f of filas || []) {
    if (!f || !CAMPOS_CON_LISTA[f.campo] || !String(f.texto ?? "").trim()) continue;
    (porCampo[f.campo] = porCampo[f.campo] || []).push(f);
  }
  for (const k of Object.keys(porCampo)) {
    porCampo[k].sort((a, b) => (Number(a.orden) || 0) - (Number(b.orden) || 0)
      || String(a.texto).localeCompare(String(b.texto)));
  }
  return porCampo;
}

const mismoTexto = (a, b) =>
  String(a ?? "").trim().toUpperCase() === String(b ?? "").trim().toUpperCase();

/* La opción que coincide con lo que dice el MIC, o `null` si lo escrito no es
 * ninguna. Con código, tienen que coincidir los dos: «JOW KON LOGISTICA» con
 * 110B no es la opción de la lista, y que el desplegable lo diga es el aviso. */
export function opcionElegida(mic, campo, opciones) {
  const def = CAMPOS_CON_LISTA[campo];
  if (!def) return null;
  return (opciones || []).find((o) => mismoTexto(o.texto, mic?.[campo])
    && (!def.codigo || mismoTexto(o.codigo, mic?.[def.codigo]))) || null;
}

/* El MIC con la opción puesta: el texto y, si el campo tiene, su código. */
export function conOpcion(mic, campo, opcion) {
  const def = CAMPOS_CON_LISTA[campo];
  if (!def || !opcion) return mic;
  const sig = { ...mic, [campo]: opcion.texto };
  if (def.codigo) sig[def.codigo] = opcion.codigo ?? "";
  return sig;
}

/* Cómo se lee una opción en el desplegable. Una ruta se nombra por el lugar de
 * carga, que es lo que la distingue («CENSER», «LOGINTER»): las nueve siguen
 * igual desde Bahía Blanca. Si dos arrancan igual, va el texto entero para no
 * ofrecer dos renglones iguales. */
export function etiquetaOpcion(opcion, todas = []) {
  const texto = String(opcion?.texto ?? "").trim();
  if (opcion?.campo === "ruta") {
    const lugar = (t) => String(t ?? "").split("/")[0].trim();
    const suyo = lugar(texto);
    const repetido = (todas || []).some((o) => o !== opcion && lugar(o.texto) === suyo);
    return suyo && !repetido ? suyo : texto;
  }
  return opcion?.codigo ? `${texto} · ${opcion.codigo}` : texto;
}

/* ── Las pólizas del campo 9 ───────────────────────────────────────────────
 *
 * Fabián, 13/10: «En el campo 9 va el nombre del propietario del Tractor y
 * otro renglón para la dirección que hoy no estoy utilizando. En ese renglón
 * necesito que se declare de manera automática los números de pólizas y su
 * vencimiento». Las escribía a mano en el domicilio: «SEGUROS
 * 58913718/58917371 VTO 01/01/2027».
 *
 * Las de cada empresa están en la base (`transportista_poliza`) y van en las
 * columnas del MIC que ya estaban para eso, `camion_poliza` y
 * `camion_poliza_vence`. El papel arma el renglón como lo escribía él.
 *
 * «SIEMPRE DEJARLO LIBRE A EDICIÓN»: es una propuesta. Se escribe encima. */
const soloDigitos = (s) => String(s ?? "").replace(/\D/g, "");

/* La patente sin espacios ni guiones: «AH 904 AN» y «AH904AN» son la misma.
 * Mismo criterio que `delPadron` en la pantalla. */
export const patenteSinAdornos = (s) =>
  String(s ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");

/* La empresa del grupo con ese CUIT, con sus pólizas, o `null`. */
export function empresaPorCuit(empresas, cuit) {
  const c = soloDigitos(cuit);
  if (c.length !== 11) return null;
  return (empresas || []).find((e) => soloDigitos(e?.cuit) === c) || null;
}

const fechaCorta = (v) => {
  const t = String(v ?? "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return "";
  const [a, m, d] = t.split("-");
  return `${d}/${m}/${a}`;
};

/* El renglón como sale impreso: «SEGUROS 58913718/58917371 VTO 01/01/2027».
 * Si alguien escribió «SEGUROS» adelante en la casilla, no sale dos veces. */
export function renglonPolizas(mic) {
  const p = String(mic?.camion_poliza ?? "").trim().replace(/^SEGUROS\s+/i, "");
  if (!p) return "";
  const v = fechaCorta(mic?.camion_poliza_vence);
  return v ? `SEGUROS ${p} VTO ${v}` : `SEGUROS ${p}`;
}

/* Las pólizas que ya escribió a mano en el renglón del domicilio, como hacía
 * hasta hoy. Ahí no se propone nada: saldrían dos veces. */
export const polizasEnDomicilio = (mic) => /^\s*SEGUROS\b/i.test(String(mic?.camion_domicilio ?? ""));

const polizasDeEmpresa = (e) => ({
  camion_poliza: e?.polizas ?? "",
  camion_poliza_vence: e?.vence ? String(e.vence).slice(0, 10) : "",
});

/* Qué pólizas van al elegir un tractor: las de la empresa dueña. Un tractor de
 * terceros no trae ninguna, aunque su dueño sea del grupo; uno de una empresa
 * sin pólizas cargadas, tampoco. En los dos casos se vacían, para que no queden
 * las del tractor anterior.
 *
 * Devuelve lo que hay que cambiar, o `{}` si no se toca nada: sin la lista de
 * empresas (la base no contestó) no se sabe, y si ya están escritas en el
 * domicilio saldrían dos veces. */
export function polizasAlElegirTractor(mic, tractor, empresas) {
  if (!tractor || !(empresas || []).length || polizasEnDomicilio(mic)) return {};
  if (tractor.tercero) return polizasDeEmpresa(null);
  return polizasDeEmpresa(empresaPorCuit(empresas, tractor.camion_cuit));
}

/* El acoplado del padrón con esa patente, o `undefined`. Sin adivinar: una
 * patente parecida no es la misma. */
export function acopladoDelPadron(acoplados, placa) {
  const p = patenteSinAdornos(placa);
  if (!p) return undefined;
  return (acoplados || []).find((a) => patenteSinAdornos(a?.placa) === p);
}

/* Se comparan los dígitos: «58913626/58913623» y «58913626 / 58913623» son
   las mismas pólizas. */
const usaLasDe = (mic, e) =>
  !!e && soloDigitos(mic?.camion_poliza) !== ""
  && soloDigitos(mic?.camion_poliza) === soloDigitos(e.polizas);

/* «En algunos casos enganchamos tractores y furgones de distintas empresas y
 * ahí realizo los cambios de los números de póliza.»
 *
 * Cuando el acoplado es de otra empresa del grupo que el tractor, se avisa.
 * Al armar el MIC sus pólizas se ponen solas (`polizasSegunElAcoplado`,
 * Fabián 14/10); el aviso ofrece volver a las del tractor.
 *
 * Devuelve `null` si no hay nada que decir: sin tractor elegido, sin acoplado
 * del padrón, acoplado de la misma empresa, o con las pólizas escritas a mano
 * en el domicilio. */
export function avisoAcoplado(mic, acoplados, empresas) {
  if (polizasEnDomicilio(mic)) return null;
  const cuitTractor = soloDigitos(mic?.camion_cuit);
  if (cuitTractor.length !== 11) return null;
  const a = acopladoDelPadron(acoplados, mic?.acoplado_placa);
  if (!a) return null;
  const deAcoplado = empresaPorCuit(empresas, a.cuit);
  if (!deAcoplado || soloDigitos(a.cuit) === cuitTractor) return null;
  const deTractor = empresaPorCuit(empresas, cuitTractor);
  return {
    placa: a.placa,
    acoplado: deAcoplado,
    tractor: deTractor,
    usandoAcoplado: usaLasDe(mic, deAcoplado),
    polizasAcoplado: polizasDeEmpresa(deAcoplado),
    polizasTractor: deTractor ? polizasDeEmpresa(deTractor) : null,
  };
}

/* LAS PÓLIZAS SEGÚN EL DUEÑO DEL FURGÓN, SOLAS (Fabián, 14/10, #395: «prefiero
 * que lo determine de manera automática cuando el furgón pertenece a otra
 * empresa distinta al tractor»). Van las dos pólizas de la empresa del
 * acoplado, como él cargó el AR.142.067595 ese día (tractor de Transportes,
 * furgón de Transcan: 58913637/58913614).
 *
 * Una vez por par tractor + acoplado: `antes` es `{ par, puestas }` de la
 * última vez. Si el par no cambió no se toca nada, así que lo corregido a mano
 * o «Volver a las del tractor» se respeta. Si cambió a un acoplado de la misma
 * empresa y siguen las que se pusieron solas, vuelven las del tractor.
 *
 * Devuelve `{ par, puestas, cambio }`: `cambio` es lo que hay que escribir en
 * el formulario (`{}` si nada). */
export const parDeUnidades = (mic) =>
  `${patenteSinAdornos(mic?.placa)}+${patenteSinAdornos(mic?.acoplado_placa)}`;

export function polizasSegunElAcoplado(mic, acoplados, empresas, antes = {}) {
  const par = parDeUnidades(mic);
  if (par === antes.par) return { par, puestas: antes.puestas ?? null, cambio: {} };
  const aviso = avisoAcoplado(mic, acoplados, empresas);
  if (aviso) {
    if (aviso.usandoAcoplado) return { par, puestas: null, cambio: {} };
    return { par, puestas: aviso.polizasAcoplado, cambio: aviso.polizasAcoplado };
  }
  const quedaron = antes.puestas
    && soloDigitos(mic?.camion_poliza) === soloDigitos(antes.puestas.camion_poliza);
  const deTractor = empresaPorCuit(empresas, mic?.camion_cuit);
  if (quedaron && deTractor && !polizasEnDomicilio(mic)) {
    return { par, puestas: null, cambio: polizasDeEmpresa(deTractor) };
  }
  return { par, puestas: null, cambio: {} };
}
